import { encode as encodeBlurhash } from 'blurhash';

/**
 * Client-side photo pipeline for /v1/business/photos: re-encodes a source
 * image into the fixed variant ladder (480/960/1600px wide) plus a re-encoded
 * original, all as JPEG. Canvas re-encoding both satisfies the backend's
 * JPEG-only contract and strips EXIF metadata along the way (the ingest
 * pipeline rejects photos that still carry it).
 */

export const LADDER_WIDTHS = { w480: 480, w960: 960, w1600: 1600 } as const;
const JPEG_QUALITY = 0.85;
const BLURHASH_SAMPLE_WIDTH = 32;
const BLURHASH_COMPONENTS_X = 4;
const BLURHASH_COMPONENTS_Y = 4;

export type PhotoVariantBlob = { blob: Blob; width: number; height: number };

export type PhotoUploadPlan = {
  original: PhotoVariantBlob;
  w480: PhotoVariantBlob;
  w960: PhotoVariantBlob;
  w1600: PhotoVariantBlob;
  blurhash: string;
};

/**
 * Pure aspect-ratio math, split out so it's testable without a real canvas:
 * the rendered height for a target width. Vision stays bounded to the source;
 * catalog variants opt into the exact widths required by the upload contract.
 */
export function computeVariantDimensions(
  sourceWidth: number,
  sourceHeight: number,
  targetWidth: number,
  exactWidth = false,
): { width: number; height: number } {
  const width = exactWidth ? targetWidth : Math.min(targetWidth, sourceWidth);
  const height = Math.max(1, Math.round((sourceHeight / sourceWidth) * width));
  return { width, height };
}

export async function buildPhotoUploadPlan(file: File): Promise<PhotoUploadPlan> {
  const bitmap = await createImageBitmap(file);
  try {
    const [original, w480, w960, w1600] = await Promise.all([
      renderVariant(bitmap, bitmap.width),
      renderVariant(bitmap, LADDER_WIDTHS.w480, true),
      renderVariant(bitmap, LADDER_WIDTHS.w960, true),
      renderVariant(bitmap, LADDER_WIDTHS.w1600, true),
    ]);
    const blurhash = computeBlurhash(bitmap);
    return { original, w480, w960, w1600, blurhash };
  } finally {
    bitmap.close();
  }
}

async function renderVariant(bitmap: ImageBitmap, targetWidth: number, exactWidth = false): Promise<PhotoVariantBlob> {
  const { width, height } = computeVariantDimensions(bitmap.width, bitmap.height, targetWidth, exactWidth);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No pudimos preparar el lienzo para la imagen.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  const blob = await canvasToJpegBlob(canvas);
  return { blob, width, height };
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error('No pudimos generar la imagen.'));
          return;
        }
        resolve(blob);
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}

/**
 * Single-width render for the floor plan: 1568px keeps vision uploads bounded,
 * so the stored object serves both display and auto-detection
 * and always fits the model's payload limit.
 */
export const FLOOR_PLAN_WIDTH = 1568;

/** One sanitized, metadata-free JPEG for a single-image vision upload. */
export async function buildVisionImageUpload(file: File): Promise<PhotoVariantBlob> {
  const bitmap = await createImageBitmap(file);
  try {
    return await renderVariant(bitmap, FLOOR_PLAN_WIDTH);
  } finally {
    bitmap.close();
  }
}

/** @deprecated Use buildVisionImageUpload for any single-image vision flow. */
export const buildFloorPlanUpload = buildVisionImageUpload;

function computeBlurhash(bitmap: ImageBitmap): string {
  const { width, height } = computeVariantDimensions(
    bitmap.width,
    bitmap.height,
    BLURHASH_SAMPLE_WIDTH,
  );
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('No pudimos preparar el lienzo para la imagen.');
  ctx.drawImage(bitmap, 0, 0, width, height);
  const imageData = ctx.getImageData(0, 0, width, height);
  return encodeBlurhash(imageData.data, width, height, BLURHASH_COMPONENTS_X, BLURHASH_COMPONENTS_Y);
}
